import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { buildCheckinDocDefinition } from "./BuildCheckinDocDefinition";
import { formatDateText } from "./CommonFunctions";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * BatchCheckinPrintDialog
 *
 * Props:
 *   open         {boolean}  - whether the dialog is visible
 *   onClose      {function} - called to close the dialog
 *   tenants      {Array}    - already-fetched tenants filtered by stayDuration === 0
 *                             Each row has: id, roomid, names, phoneNumbers,
 *                             checkin, checkout, rate, rentalType, deposit, adults, children
 */
export default function BatchCheckinPrintDialog({ open, onClose, tenants }) {
  // All selected by default — keyed by tenancy id
  const [selected, setSelected] = useState(() =>
    Object.fromEntries((tenants || []).map((t) => [t.id, true]))
  );
  const [printing, setPrinting] = useState(false);

  // Re-init selection when tenants prop changes (dialog re-opens)
  React.useEffect(() => {
    setSelected(Object.fromEntries((tenants || []).map((t) => [t.id, true])));
  }, [tenants, open]);

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const allChecked = selectedCount === tenants.length;
  const someChecked = selectedCount > 0 && selectedCount < tenants.length;

  const toggleAll = () => {
    const next = !allChecked;
    setSelected(Object.fromEntries(tenants.map((t) => [t.id, next])));
  };

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ── Compute next tenancy dates based on current checkout + rentalType ──
  const getNextDates = (currentCheckout, rentalType) => {
    const checkin = currentCheckout; // next checkin = current checkout date
    const checkout = new Date(currentCheckout);
    if (rentalType === "monthly") checkout.setDate(checkout.getDate() + 30);
    else if (rentalType === "daily") checkout.setDate(checkout.getDate() + 1);
    else checkout.setDate(checkout.getDate() + 7); // weekly default
    return { checkin, checkout: checkout.toISOString().split("T")[0] };
  };

  // ── Parse flat strings into customer objects ─────────────────
  const parseCustomers = (names = "", phoneNumbers = "") => {
    const nameList = names.split(",").map((n) => n.trim());
    const phoneList = phoneNumbers.split(",").map((p) => p.trim());
    return nameList.map((fullName, i) => {
      const [fname, ...rest] = fullName.split(" ");
      return {
        fname: fname || "",
        lname: rest.join(" ") || "",
        phone: phoneList[i] || "",
        email: "",
      };
    });
  };

  // ── Print ─────────────────────────────────────────────────────
  const handlePrint = async () => {
    const toPrint = tenants.filter((t) => selected[t.id]);
    if (toPrint.length === 0) return;

    setPrinting(true);
    try {
      // Build each doc definition and merge content with page breaks
      const combined = { content: [], styles: null };

      toPrint.forEach((tenant, idx) => {
        const customers = parseCustomers(tenant.names, tenant.phoneNumbers);
        const { checkin, checkout } = getNextDates(tenant.checkout, tenant.rentalType);
        const def = buildCheckinDocDefinition(
          {
            roomid: tenant.roomid,
            checkin,
            checkout,
            rate: tenant.rate,
            rentalType: tenant.rentalType,
            deposit: tenant.deposit,
            adults: tenant.adults,
            children: tenant.children,
          },
          customers
        );

        // Capture styles from first form (they're identical for all)
        if (idx === 0) {
          combined.styles = def.styles;
        }

        // Add page break before every form except the first
        if (idx > 0 && def.content.length > 0) {
          def.content[0] = {
            ...def.content[0],
            pageBreak: "before",
          };
        }

        combined.content.push(...def.content);
      });

      // Open merged PDF in new tab for printing
      pdfMake.createPdf(combined).open();
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Print Check-In Forms
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Subheader ── */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={toggleAll}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
            />
          }
          label={
            <Typography variant="body" fontWeight={600}>
              Select All
            </Typography>
          }
        />
        <Chip
          label={`${selectedCount} of ${tenants.length} selected`}
          size="small"
          color={selectedCount > 0 ? "primary" : "default"}
          variant="outlined"
        />
      </Box>

      <Divider />

      {/* ── Room list ── */}
      <DialogContent sx={{ px: 2, py: 1, maxHeight: 420, overflowY: "auto" }}>
        {tenants.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography variant="body" color="text.secondary">
              No checkouts today.
            </Typography>
          </Box>
        ) : (
          tenants.map((tenant) => (
            <Box
              key={tenant.id}
              onClick={() => toggleOne(tenant.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: selected[tenant.id] ? "#e8f4fd" : "transparent",
                border: selected[tenant.id]
                  ? "1px solid #90caf9"
                  : "1px solid transparent",
                mb: 0.5,
                transition: "all 0.15s ease",
                "&:hover": { backgroundColor: selected[tenant.id] ? "#dceefb" : "#f5f5f5" },
              }}
            >
              <Checkbox
                checked={!!selected[tenant.id]}
                onChange={() => toggleOne(tenant.id)}
                onClick={(e) => e.stopPropagation()}
                size="small"
              />

              {/* Room badge */}
              <Box
                sx={{
                  minWidth: 44,
                  height: 44,
                  borderRadius: 2,
                  backgroundColor: selected[tenant.id] ? "#1976d2" : "#e0e0e0",
                  color: selected[tenant.id] ? "#fff" : "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  transition: "all 0.15s ease",
                  flexShrink: 0,
                }}
              >
                {tenant.roomid}
              </Box>

              {/* Guest info */}
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="body"
                  fontWeight={600}
                  noWrap
                  title={tenant.names}
                >
                  {tenant.names || "—"}
                </Typography><br></br>
                <Typography variant="caption" color="text.secondary">
                  {tenant.phoneNumbers || "—"}
                </Typography>
              </Box>

              {/* Next checkout date */}
              <Box textAlign="right" flexShrink={0}>
                <Typography variant="caption" color="text.secondary">
                Checkout
                </Typography><br></br>
                <Typography variant="body" fontWeight={500}>
                  {formatDateText(tenant.checkout)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>

      <Divider />

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={printing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            printing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PrintIcon />
            )
          }
          onClick={handlePrint}
          disabled={selectedCount === 0 || printing}
        >
          {printing
            ? "Generating..."
            : `Print ${selectedCount} Form${selectedCount !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}